<script setup lang="ts">
import ApplicationBase from '@/components/Applications/ApplicationBase.vue'
import { useJoakimStore } from '@/stores/useJoakimStore'

import BeginStep from './Steps/ApplicationUtgreidslaSereignUmsoknBegin.vue'
import Umsaekjandi from '../Shared/Umsaekjandi.vue'
import Tegund from './Steps/Step3.vue'
import StadfestingOrorku from './Steps/Step3_1.vue'
import UpplysingarUmLatna from './Steps/Step3_2.vue'
import Bankareikningur from './Steps/Step4.vue'
import Greidslutilhogun from './Steps/Step5.vue'
import StadgreidslaSkatta from './Steps/Step6.vue'
import PEP from '../Shared/PEP.vue'
import Step7 from './Steps/Step7.vue'
import Undirritun from '../Shared/Undirritun.vue'


const APP_ID = 4
const UPDATE_KEY = 'utgreidslusereignumsokn'

type UmsoknLite = { tegund?: number; bankNumber?: string; ledger?: string; accountNumber?: string }
const asNum = (u?: { tegund?: unknown } | null) => Number((u?.tegund ?? NaN))
const isAny = (u: UmsoknLite | null | undefined, list: number[]) => list.includes(asNum(u))

const joakim = useJoakimStore()

async function runBankCheck(umsokn: UmsoknLite | null) {
  const t = umsokn?.tegund
  const shouldCheck = t === 0 || t === 1 || t === 4
  if (!shouldCheck) return { valid: true, errorMessage: '' }
  const res = await joakim.checkBankaReikning(umsokn)
  const data = (res as any)?.data ?? res
  const valid = !!data?.valid
  return { valid, errorMessage: valid ? '' : (data?.message || 'Ógildur bankareikningur') }
}

const steps = [
  { comp: BeginStep, titleKey: 'utgreidsla_sereign.skref1.title' },
  { comp: Umsaekjandi, titleKey: 'umsokn_umsaekjandi_header' },
  { comp: Tegund, titleKey: 'utgreidsla_umsokn_step2_tegund' },

  // 3.x — conditional details immediately after Tegund
  { comp: UpplysingarUmLatna,  titleKey: 'utgreidsla_umsokn_upplysingar_latna_header1', when: ({ umsokn }: { umsokn: UmsoknLite | null }) => asNum(umsokn) === 2 },
  { comp: StadfestingOrorku,   titleKey: 'utgreidsla_umsokn_stadfestingororku_header1', when: ({ umsokn }: { umsokn: UmsoknLite | null }) => asNum(umsokn) === 1 },

  // 4: Bankareikningur
  { comp: Bankareikningur, titleKey: 'umsokn_bankareikningur_header1', when: ({ umsokn }: { umsokn: UmsoknLite | null }) => isAny(umsokn, [0,1,2,3,4]), props: { runBankCheck } },

  // 5..7
  { comp: Greidslutilhogun,   titleKey: 'utgreidsla_umsokn_greidslutilhogun_header', when: ({ umsokn }: { umsokn: UmsoknLite | null }) => isAny(umsokn, [0,1,2,4]) },
  { comp: StadgreidslaSkatta, titleKey: 'utgreidsla_umsokn_stadgreidslaskatta_header', when: ({ umsokn }: { umsokn: UmsoknLite | null }) => isAny(umsokn, [0,1,2,3,4]) },
  { comp: PEP,                titleKey: 'umsokn_PEP_header', props: { showDisclamer: true }, when: ({ umsokn }: { umsokn: UmsoknLite | null }) => isAny(umsokn, [0,1,2,3,4]) },
  { comp: Step7,              titleKey: 'utgreidsla_umsokn_samantekt_header', when: ({ umsokn }: { umsokn: UmsoknLite | null }) => isAny(umsokn, [0,1,2,3,4])  },
  { comp: Undirritun,   titleKey: 'undirritun_umsokn_header1', when: ({ umsokn }: { umsokn: UmsoknLite | null }) => isAny(umsokn, [0,1,2,3,4])  },
]
</script>

<template>
  <ApplicationBase :app-id="APP_ID" :update-key="UPDATE_KEY" :steps="steps" />
</template>
